import { useState } from 'react';
import { useContent } from '../../../content/ContentProvider';

export default function ContactSection() {
  const content = useContent();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setSubmitMessage('Please fill in all required fields');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setSubmitMessage('Please enter a valid email address');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitMessage('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formDataEncoded = new URLSearchParams();
      formDataEncoded.append('name', formData.name);
      formDataEncoded.append('email', formData.email);
      formDataEncoded.append('phone', formData.phone);
      formDataEncoded.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6qca8ra90d3mf3egfcg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataEncoded.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you! We\'ll get back to you soon.');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <h3
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Get In Touch
            </h3>
            <p className="text-xl text-gray-600 mb-12" style={{ fontFamily: 'Inter, sans-serif' }}>
              Have questions? We'd love to hear from you
            </p>

            <div className="space-y-6">
              {content.global.contactInfo.map((info, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${info.icon} text-xl text-teal-600`}></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">
                      {info.type === 'email' ? 'Email' : info.type === 'tel' ? 'Phone' : 'Address'}
                    </div>
                    {info.type === 'email' ? (
                      <a href={`mailto:${info.value}`} className="text-gray-600 hover:text-teal-600 transition-colors">
                        {info.value}
                      </a>
                    ) : info.type === 'tel' ? (
                      <a href={`tel:${info.value.replace(/[^0-9+]/g, '')}`} className="text-gray-600 hover:text-teal-600 transition-colors">
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-gray-600">
                        {content.brand.fullName}<br />
                        {info.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4 mt-12">
              {content.global.socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.link}
                  rel="nofollow"
                  className="w-12 h-12 rounded-full bg-gray-100 hover:bg-teal-600 flex items-center justify-center transition-colors group cursor-pointer"
                >
                  <i className={`${social.icon} text-xl text-gray-600 group-hover:text-white transition-colors`}></i>
                </a>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-3xl p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-5 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent bg-white text-base"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-5 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent bg-white text-base"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-5 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent bg-white text-base"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                  Message * (Max 500 characters)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  maxLength={500}
                  className="w-full px-5 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent bg-white resize-none text-base"
                  required
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length}/500
                </div>
              </div>

              {submitMessage && (
                <p className={`text-sm ${submitMessage.includes('Thank you') ? 'text-teal-600' : 'text-red-600'}`}>
                  {submitMessage}
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}